import React from 'react'
import {
  Flex,
  FormControl,
  FormLabel,
  Input,
  Select,
  IconButton,
  Box
} from '@chakra-ui/react'
import { useIntl } from 'react-intl'
import { TrashIcon } from '@heroicons/react/24/outline'
import { type Medication } from './types'
import { medicationRowMessages } from './messages'

interface MedicationRowProps {
  medication: Medication
  onUpdate: (updatedMedication: Medication) => void
  onDelete: () => void
  index: number
}

const MedicationRow: React.FC<MedicationRowProps> = ({
  medication,
  onUpdate,
  onDelete,
  index
}) => {
  const { formatMessage } = useIntl()

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target
    onUpdate({
      ...medication,
      [name]: value
    })
  }

  const frequencyOptions = [
    'Once daily',
    'Twice daily',
    'Three times daily',
    'Four times daily',
    'Every morning',
    'Every evening',
    'Every hour',
    'Every 4 hours',
    'Every 6 hours',
    'Every 8 hours',
    'Every 12 hours',
    'As needed',
    'With meals',
    'Before meals',
    'After meals',
    'Other'
  ]

  return (
    <Box
      p={4}
      borderWidth="1px"
      borderRadius="md"
      mb={4}
      bg="white"
      boxShadow="sm"
    >
      <Flex direction="column" gap={3}>
        <Flex gap={4} align="center">
          <Box fontWeight="bold" minWidth="30px">
            {index + 1}.
          </Box>
          <FormControl>
            <FormLabel fontSize="sm">
              {formatMessage(medicationRowMessages.medicationLabel)}
            </FormLabel>
            <Input
              name="name"
              value={medication.name}
              onChange={handleChange}
              placeholder={formatMessage(
                medicationRowMessages.medicationPlaceholder
              )}
              size="sm"
            />
          </FormControl>
          <FormControl>
            <FormLabel fontSize="sm">
              {formatMessage(medicationRowMessages.concentrationLabel)}
            </FormLabel>
            <Input
              name="concentration"
              value={medication.concentration}
              onChange={handleChange}
              placeholder={formatMessage(
                medicationRowMessages.concentrationPlaceholder
              )}
              size="sm"
            />
          </FormControl>
          <IconButton
            aria-label={formatMessage(
              medicationRowMessages.removeMedicationAriaLabel
            )}
            icon={<TrashIcon style={{ width: '20px', height: '20px' }} />}
            size="sm"
            colorScheme="red"
            variant="ghost"
            onClick={onDelete}
          />
        </Flex>

        <Flex gap={4}>
          <FormControl>
            <FormLabel fontSize="sm">
              {formatMessage(medicationRowMessages.quantityLabel)}
            </FormLabel>
            <Input
              name="quantity"
              value={medication.quantity}
              onChange={handleChange}
              placeholder={formatMessage(
                medicationRowMessages.quantityPlaceholder
              )}
              size="sm"
            />
          </FormControl>
          <FormControl>
            <FormLabel fontSize="sm">
              {formatMessage(medicationRowMessages.frequencyLabel)}
            </FormLabel>
            <Select
              name="frequency"
              value={medication.frequency}
              onChange={handleChange}
              placeholder={formatMessage(
                medicationRowMessages.frequencyPlaceholder
              )}
              size="sm"
            >
              {frequencyOptions.map((option) => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
            </Select>
          </FormControl>
          <FormControl>
            <FormLabel fontSize="sm">
              {formatMessage(medicationRowMessages.durationLabel)}
            </FormLabel>
            <Input
              name="duration"
              value={medication.duration}
              onChange={handleChange}
              placeholder={formatMessage(
                medicationRowMessages.durationPlaceholder
              )}
              size="sm"
            />
          </FormControl>
        </Flex>

        <FormControl>
          <FormLabel fontSize="sm">
            {formatMessage(medicationRowMessages.specialInstructionsLabel)}
          </FormLabel>
          <Input
            name="instructions"
            value={medication.instructions}
            onChange={handleChange}
            placeholder={formatMessage(
              medicationRowMessages.specialInstructionsPlaceholder
            )}
            size="sm"
          />
        </FormControl>
      </Flex>
    </Box>
  )
}

export default MedicationRow
